/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ScanNetworkPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Devices module.
----------------------------------------------------------------*/


import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Check, Save } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import * as api from './api';
import * as vendorsApi from '../provisioning-vendors/api';
import * as modelsApi from '../provisioning-device-models/api';
import * as templatesApi from '../provisioning-templates/api';
import { fetchExtensions } from '../extensions/api/extensionsApi';
import Select from '../../components/ui/select';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';

interface ScannedDeviceState {
    ip: string;
    mac: string;
    description: string;
    detectedVendor?: string;
    matchedVendorId?: number;
    selectedModelId?: number;
    selectedTemplateId?: number;
    selectedExtensionId?: number;
    isSaved?: boolean;
}

export default function ScanNetworkPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const [cidr, setCidr] = useState('192.168.1.0/24');
    const [isScanning, setIsScanning] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Data Sources
    const [vendors, setVendors] = useState<any[]>([]);
    const [models, setModels] = useState<any[]>([]);
    const [templates, setTemplates] = useState<any[]>([]);
    const [extensions, setExtensions] = useState<any[]>([]);

    // Scan Results
    const [scannedDevices, setScannedDevices] = useState<ScannedDeviceState[]>([]);

    // Selection
    const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

    useEffect(() => {
        const loadMetadata = async () => {
            try {
                const [vData, mData, tData, eData, networkData] = await Promise.all([
                    vendorsApi.getVendors(),
                    modelsApi.getDeviceModels(),
                    templatesApi.getTemplates(),
                    fetchExtensions({ page: 1, limit: 1000, sortBy: 'extension', sortOrder: 'asc' }),
                    api.getLocalNetworkCidr()
                ]);
                setVendors(vData);
                setModels(mData);
                setTemplates(tData);
                setExtensions(eData.data || []);
                if (networkData && networkData.cidr) {
                    setCidr(networkData.cidr);
                }
            } catch (err) {
                console.error("Failed to load provisioning metadata", err);
                showError(t('provisioning.metadataError', 'Failed to load configuration data.'));
            }
        };
        loadMetadata();
    }, []);

    const handleScan = async () => {
        setIsScanning(true);
        setError(null);
        setScannedDevices([]);
        setSelectedIndices(new Set());
        try {
            const results = await api.scanNetwork(cidr);

            // Filter and Process Results
            const processed: ScannedDeviceState[] = [];

            for (const res of results) {
                // vendor matching logic
                let matchedVendorId: number | undefined;
                const macClean = res.mac.replace(/[:.-]/g, '').toLowerCase();

                // Find vendor by MAC prefix
                const matchedVendor = vendors.find(v => {
                    if (!v.macPrefixes || !Array.isArray(v.macPrefixes)) return false;
                    return v.macPrefixes.some((prefix: string) =>
                        macClean.startsWith(prefix.replace(/[:.-]/g, '').toLowerCase())
                    );
                });

                if (matchedVendor) {
                    matchedVendorId = matchedVendor.id;
                    processed.push({
                        ip: res.ip,
                        mac: res.mac,
                        description: '',
                        detectedVendor: res.vendor,
                        matchedVendorId: matchedVendorId,
                        selectedModelId: undefined,
                        selectedTemplateId: undefined,
                        selectedExtensionId: undefined,
                        isSaved: false
                    });
                }
            }

            if (processed.length === 0) {
                if (results.length > 0) {
                    setError(t('provisioningDevices.noMatchingVendors', 'Devices found, but none match your configured Vendor MAC prefixes.'));
                } else {
                    setError(t('provisioningDevices.scanNoResults', 'No devices found in this range.'));
                }
            } else {
                setScannedDevices(processed);
            }

        } catch (err) {
            console.error('Scan error:', err);
            setError(t('provisioningDevices.scanError', 'Failed to scan network.'));
        } finally {
            setIsScanning(false);
        }
    };

    const handleUpdateDevice = <K extends keyof ScannedDeviceState>(idx: number, field: K, value: ScannedDeviceState[K]) => {
        setScannedDevices(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };

            // Auto-reset dependent fields
            if (field === 'matchedVendorId') {
                next[idx].selectedModelId = undefined;
                next[idx].selectedTemplateId = undefined;
            }
            if (field === 'selectedModelId') {
                next[idx].selectedTemplateId = undefined;
            }
            return next;
        });

    };

    const toggleSelection = (idx: number) => {
        setSelectedIndices(prev => {
            const next = new Set(prev);
            if (next.has(idx)) {
                next.delete(idx);
            } else {
                next.add(idx);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIndices.size === scannedDevices.length && scannedDevices.length > 0) {
            setSelectedIndices(new Set());
        } else {
            const all = new Set(scannedDevices.map((_, idx) => idx));
            setSelectedIndices(all);
        }
    };

    const handleBulkProvision = async () => {
        const indices = Array.from(selectedIndices);
        if (indices.length === 0) return;

        // Check validation
        const invalid = indices.some(idx => {
            const d = scannedDevices[idx];
            return !d.matchedVendorId || !d.selectedModelId || !d.selectedTemplateId || !d.selectedExtensionId;
        });

        if (invalid) {
            showError(t('provisioning.bulkInvalid', 'Please complete configuration for all selected devices.'));
            return;
        }

        let successCount = 0;
        let failCount = 0;

        for (const idx of indices) {
            const device = scannedDevices[idx];
            if (device.isSaved) continue;

            try {
                const payload = {
                    macAddress: device.mac,
                    vendorId: device.matchedVendorId,
                    modelId: device.selectedModelId,
                    templateId: device.selectedTemplateId,
                    ipAddress: device.ip,
                    description: device.description || `Auto-provisioned ${device.mac}`,
                    accounts: [
                        {
                            id: 1, // Primary account index
                            label: "Account 1",
                            extensionId: device.selectedExtensionId,
                            isEnabled: true
                        }
                    ]
                };

                await api.createDevice(payload);
                handleUpdateDevice(idx, 'isSaved', true);
                successCount++;
            } catch (err) {
                console.error(`Failed to provision ${device.mac}`, err);
                failCount++;
            }
        }

        if (successCount > 0) {
            showSuccess(t('provisioning.bulkSuccess', `Successfully provisioned ${successCount} devices.`));
            setSelectedIndices(new Set());
        }
        if (failCount > 0) {
            showError(t('provisioning.bulkPartialError', `Failed to provision ${failCount} devices.`));
        }
    };

    const handleCancel = () => {
        navigate('/settings/provisioning/provisioning-devices');
    };

    // Helper options builders
    const vendorOptions = useMemo(() => {
        return vendors.map(v => ({
            value: String(v.id),
            label: v.name
        }));
    }, [vendors]);

    const getModelsForVendor = (vendorId?: number) => {
        if (!vendorId) return [];
        return models.filter(m => m.vendorId === vendorId).map(m => ({
            value: String(m.id),
            label: m.model
        }));
    };

    const getTemplatesForModel = (modelId?: number) => {
        if (!modelId) return [];
        return templates.filter(t => t.modelId === modelId).map(t => ({
            value: String(t.id),
            label: t.name
        }));
    };

    const extensionOptions = useMemo(() => {
        return extensions.map(e => ({
            value: String(e.id),
            label: `${e.extension} - ${e.name}`
        }));
    }, [extensions]);

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Search className="h-5 w-5 text-primary" />}
                title={t('provisioningDevices.scanNetwork', 'Scan Network')}
                backButtonText={t('common.list', 'List')}
                onBack={handleCancel}
                actions={[]}
            />

            <div className="mt-8">
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
                    <div className="flex gap-4 items-center mb-6">
                        <label className="text-sm font-medium w-24">Networks</label>
                        <div className="flex gap-2 flex-1 max-w-xl">
                            <input
                                type="text"
                                value={cidr}
                                onChange={(e) => setCidr(e.target.value)}
                                placeholder="e.g. 192.168.1.0/24"
                                className="flex-1 px-3 py-2 border rounded-md bg-background"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !isScanning && cidr) {
                                        handleScan();
                                    }
                                }}
                            />
                            <button
                                onClick={handleScan}
                                disabled={isScanning || !cidr}
                                className="px-4 py-2 bg-primary text-primary-foreground rounded-md disabled:opacity-50 flex items-center gap-2 min-w-[100px] justify-center hover:bg-primary/90 transition-colors"
                            >
                                {isScanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                                {t('common.scan', 'Scan')}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-6 p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    <div className="min-h-[300px]">
                        {isScanning && scannedDevices.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                                <Loader2 className="h-8 w-8 animate-spin mb-2" />
                                <p>{t('provisioningDevices.scanning', 'Scanning network...')}</p>
                            </div>
                        )}

                        {!isScanning && scannedDevices.length === 0 && !error && (
                            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                                <Search className="h-8 w-8 mb-2 opacity-50" />
                                <p>{t('provisioningDevices.enterRange', 'Enter a CIDR range and click Scan')}</p>
                            </div>
                        )}

                        {scannedDevices.length > 0 && (
                            <div>
                                <h3 className="text-lg font-medium mb-4">{t('provisioningDevices.detectedDevices', 'Detected Devices')}</h3>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-transparent border-b">
                                            <tr>
                                                <th className="px-4 py-3 w-10">
                                                    <input
                                                        type="checkbox"
                                                        className="rounded border-gray-300"
                                                        checked={scannedDevices.length > 0 && selectedIndices.size === scannedDevices.length}
                                                        onChange={toggleSelectAll}
                                                    />
                                                </th>
                                                <th className="px-2 py-3 text-left font-medium w-40">{t('provisioningDevices.mac', 'MAC Address')}</th>
                                                <th className="px-2 py-3 text-left font-medium">{t('common.description', 'Description')}</th>
                                                <th className="px-2 py-3 text-left font-medium w-32">{t('provisioningDevices.ip', 'IP Address')}</th>
                                                <th className="px-2 py-3 text-left font-medium w-40">{t('provisioningDevices.brand', 'Brand')}</th>
                                                <th className="px-2 py-3 text-left font-medium w-48">{t('provisioningDevices.model', 'Model')}</th>
                                                <th className="px-2 py-3 text-left font-medium w-48">{t('provisioningDevices.template', 'Template')}</th>
                                                <th className="px-2 py-3 text-left font-medium w-48">{t('provisioningDevices.account', 'Account')}</th>
                                                <th className="px-2 py-3 text-right font-medium w-10"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y-0 text-gray-500">
                                            {scannedDevices.map((device, idx) => {
                                                const modelOptions = getModelsForVendor(device.matchedVendorId);
                                                const templateOptions = getTemplatesForModel(device.selectedModelId);

                                                return (
                                                    <tr key={idx} className={`transition-colors ${device.isSaved ? 'opacity-50' : ''}`}>
                                                        <td className="px-4 py-3 align-middle">
                                                            <input
                                                                type="checkbox"
                                                                className="rounded border-gray-300"
                                                                checked={selectedIndices.has(idx)}
                                                                onChange={() => toggleSelection(idx)}
                                                                disabled={!!device.isSaved}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <input
                                                                type="text"
                                                                value={device.mac}
                                                                readOnly
                                                                className="w-full px-3 py-2 border rounded-md bg-muted/20 text-xs font-mono"
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <input
                                                                type="text"
                                                                value={device.description}
                                                                onChange={(e) => handleUpdateDevice(idx, 'description', e.target.value)}
                                                                className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                                                                disabled={!!device.isSaved}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2 text-sm">{device.ip}</td>
                                                        <td className="px-2 py-2">
                                                            <Select
                                                                options={vendorOptions}
                                                                value={device.matchedVendorId ? String(device.matchedVendorId) : ''}
                                                                onValueChange={(val) => handleUpdateDevice(idx, 'matchedVendorId', Number(val))}
                                                                placeholder={t('common.select', 'Select...')}
                                                                className="w-full"
                                                                disabled={!!device.isSaved}
                                                                searchable
                                                            />
                                                        </td>

                                                        <td className="px-2 py-2">
                                                            <Select
                                                                options={modelOptions}
                                                                value={device.selectedModelId ? String(device.selectedModelId) : ''}
                                                                onValueChange={(val) => handleUpdateDevice(idx, 'selectedModelId', Number(val))}
                                                                placeholder={t('common.select', 'Select...')}
                                                                className="w-full"
                                                                disabled={!device.matchedVendorId || !!device.isSaved}
                                                                searchable
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <Select
                                                                options={templateOptions}
                                                                value={device.selectedTemplateId ? String(device.selectedTemplateId) : ''}
                                                                onValueChange={(val) => handleUpdateDevice(idx, 'selectedTemplateId', Number(val))}
                                                                placeholder={t('common.select', 'Select...')}
                                                                className="w-full"
                                                                disabled={!device.selectedModelId || !!device.isSaved}
                                                                searchable
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <Select
                                                                options={extensionOptions}
                                                                value={device.selectedExtensionId ? String(device.selectedExtensionId) : ''}
                                                                onValueChange={(val) => handleUpdateDevice(idx, 'selectedExtensionId', Number(val))}
                                                                placeholder={t('common.none', 'None')}
                                                                className="w-full"
                                                                disabled={!!device.isSaved}
                                                                searchable
                                                            />
                                                        </td>

                                                        <td className="px-2 py-3 text-right sticky right-0 bg-transparent">
                                                            {device.isSaved && (
                                                                <span className="text-green-600 flex justify-end">
                                                                    <Check className="h-4 w-4" />
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <FixedActionBar
                onSave={handleBulkProvision}
                onCancel={handleCancel}
                saveText={`${t('common.saveSelected', 'Save Selected')} (${selectedIndices.size})`}
                saveDisabled={selectedIndices.size === 0}
                showDelete={false}
                cancelText={t('common.cancel', 'Cancel')}
            />
        </div>
    );
}
